import { Card, ConfigProvider, Divider, Table, Typography } from 'antd'
import { ColumnsType, TableProps } from 'antd/es/table'
import CheckableTag from 'antd/es/tag/CheckableTag'
import { IndicationClassification } from 'features/indicationTriage'
import _ from 'lodash'
import React, { FC, ReactNode, useMemo } from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import {
  GeckoFormattingVariant,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  GeckoView,
} from 'types'

import {
  IndicationColorState,
  UNKNOWN_COLOR_SCHEME,
} from './indicationClassificationColors'
import {
  createClassificationAntConfig,
  createClassificationCheckableTagStyle,
} from './IndicationClassificationTag'
import {
  ClassificationDef,
  IndicationClassificationConfig,
} from './indicationClassifier'

export interface IndicationClassificationConfigVisualizerProps {
  config: IndicationClassificationConfig
  colors: Record<string, IndicationColorState>
  tableProps?: Omit<
    TableProps<{
      threshold?: number
      description?: string
      label: IndicationClassification
    }>,
    'columns' | 'dataSource'
  >
}

const { Text } = Typography

const TableContainer = styled.div`
  display: flex;
  width: 100%;
`

const ClassificationTagLegendItemContainer = styled.div`
  width: 8rem;
`
type RecType = {
  threshold?: number
  description?: string
  label: IndicationClassification
}

function getTableColumns(
  config: IndicationClassificationConfig,
  colorThemes: Record<string, IndicationColorState>
): ColumnsType<RecType> {
  // If none of the classifications specify a threshold, we hide those columns
  const showThresholds = !!_.find(
    Object.values(config.classifications),
    (classificationDef: ClassificationDef) =>
      classificationDef.threshold !== undefined
  )
  // If none of the classifications specify a description, we hide those columns
  const showDescriptions = !!_.find(
    Object.values(config.classifications),
    (classificationDef: ClassificationDef) => !!classificationDef.description
  )

  const unit =
    GeckoMeasurementUnit.GetByID(config.unitId) ?? GeckoMeasurementUnit.None
  const sortedClassifications = _.sortBy(
    Object.keys(config.classifications),
    (indicationClassification: IndicationClassification) =>
      (config.classifications[indicationClassification]?.threshold ?? 0) *
      (config.ordering === 'desc' ? -1 : 1)
  )

  /** Figure out the "max value" of a classification based on the ordering */
  function getMin(
    indicationClassification: IndicationClassification
  ): number | null {
    if (config.ordering === 'desc') {
      return config.classifications[indicationClassification]?.threshold ?? null
    }
    const index = _.indexOf(sortedClassifications, indicationClassification)
    return index <= 0
      ? null
      : (config.classifications[sortedClassifications[index - 1]]?.threshold ??
          null)
  }

  /** Figure out the "min value" of a classification based on the ordering */
  function getMax(
    indicationClassification: IndicationClassification
  ): number | null {
    if (config.ordering !== 'desc') {
      return config.classifications[indicationClassification]?.threshold ?? null
    }
    const index = _.indexOf(sortedClassifications, indicationClassification)
    return index >= sortedClassifications.length - 1
      ? null
      : (config.classifications[sortedClassifications[index + 1]]?.threshold ??
          null)
  }

  /** Render a value as a Measurement label with the unit attached */
  function measurementLabel(val: number | null): ReactNode {
    if (val === null || val === undefined) {
      return <></>
    }
    const meas = GeckoMeasurement.CreateFromValue(val, unit)
    return <GeckoMeasurementLabel measurement={meas} variant='narrow' />
  }

  return [
    // First column, render the label
    {
      dataIndex: 'label',
      key: 'label',
      render: (label: IndicationClassification) => {
        const tagColorTheme = colorThemes[label] ?? UNKNOWN_COLOR_SCHEME
        const tagStyle = createClassificationCheckableTagStyle(
          tagColorTheme,
          false
        )
        return (
          <ClassificationTagLegendItemContainer>
            <ConfigProvider
              theme={createClassificationAntConfig(tagColorTheme)}>
              <CheckableTag style={tagStyle} checked={false}>
                {label}
              </CheckableTag>
            </ConfigProvider>
          </ClassificationTagLegendItemContainer>
        )
      },
    },
    // Conditionally show the measurement range column if thresholds are defined
    ...(showThresholds
      ? [
          {
            key: 'measurement_range',
            render: (_: unknown, { label }: RecType) => {
              const min: number | null = getMin(label)
              const max: number | null = getMax(label)
              if (min !== null && max !== null) {
                return (
                  <>
                    {measurementLabel(min)} - {measurementLabel(max)}
                  </>
                )
              }
              if (min !== null) {
                return <>≥ {measurementLabel(min)}</>
              }
              if (max !== null) {
                return <>≤ {measurementLabel(max)}</>
              }
              return <div />
            },
          },
        ]
      : []),
    // Conditionally show the description column if descriptions are defined
    ...(showDescriptions
      ? [
          {
            dataIndex: 'description',
            key: 'description',
            render: (description: string) => {
              return <Text>{description}</Text>
            },
          },
        ]
      : []),
  ]
}

const RawIndicationClassificationConfigVisualizer: FC<
  IndicationClassificationConfigVisualizerProps
> = ({ config, colors: colorThemes, tableProps }) => {
  const geckoView = useMemo(
    () => (config.viewId ? GeckoView.GetByID(config.viewId) : null) ?? null,
    [config.viewId]
  )

  const columns = useMemo(
    () => getTableColumns(config, colorThemes),
    [colorThemes, config]
  )

  const tableRows = useMemo(
    () =>
      _.sortBy(
        Object.entries(config.classifications).map(
          ([key, value]: [IndicationClassification, ClassificationDef]) => ({
            label: key,
            threshold: value.threshold,
            description: value.description,
          })
        ),
        (row) => (row.threshold ?? 0) * (config.ordering === 'desc' ? -1 : 1)
      ),
    [config.classifications, config.ordering]
  )

  const { theme } = useGeckoTheme()

  return (
    <Card
      style={{ width: '100%' }}
      size='small'
      title={
        <>
          <Text>{config.title ?? geckoView?.name ?? 'Unknown'}</Text>
          {!!geckoView && (
            <>
              {' '}
              <Divider type='vertical' /> <Text strong>View:</Text>{' '}
              <Text style={{ color: theme.forecast.text4 }}>
                {geckoView?.name}
              </Text>
            </>
          )}
        </>
      }>
      <TableContainer>
        <Table
          style={{ width: '100%' }}
          showHeader={false}
          size={'small'}
          columns={columns}
          dataSource={tableRows}
          pagination={false}
          bordered={false}
          rowKey={(row) => row.label}
          {...tableProps}></Table>
      </TableContainer>
    </Card>
  )
}

export const IndicationClassificationConfigVisualizer = React.memo(
  RawIndicationClassificationConfigVisualizer
)

import { useGeckoLocale } from 'features/locale'

/**
 * A label component which takes in a measurement and formats it in a
 * user-friendly way, taking into account the current measurement system
 * (imperial or metric) and a number of formatting options
 * @param measurement     The measurement or measurement range to format
 * @param variant         The string variant to use (affects string length)
 * @param showLoading     Whether to show a "Loading..." filler string
 */
export function useGeckoMeasurementLabelText(
  measurement: GeckoMeasurement | GeckoMeasurementRange | undefined,
  variant?: GeckoFormattingVariant | 'none',
  showLoading?: boolean
): string | undefined {
  const { formatMeasurement, formatMeasurementRange } = useGeckoLocale()
  if (!measurement) return undefined
  if (showLoading) return 'Loading…'
  if (measurement instanceof GeckoMeasurement) {
    return formatMeasurement({ measurement, variant, showLoading })
  }

  return formatMeasurementRange({ measurement, variant, showLoading })
}

/**
 * A label component which takes in a measurement and formats it in a
 * user-friendly way, taking into account the current measurement system
 * (imperial or metric) and a number of formatting options
 * @param measurement     The measurement or measurement range to format
 * @param variant         The string variant to use (affects string length)
 * @param options         Any additional options used for string formatting
 * @param showLoading     Whether to show a "Loading..." filler string
 * @param className       A class name, used for things like styled formatters
 * @param div             Whether to treat this as a div or a span (default)
 */
export const GeckoMeasurementLabel = React.forwardRef<
  HTMLDivElement,
  {
    measurement: GeckoMeasurement | GeckoMeasurementRange | undefined
    variant?: GeckoFormattingVariant | 'none'
    showLoading?: boolean
    className?: string | undefined
    div?: true // Default value is false
  }
>(({ div, measurement, variant, showLoading, ...rest }, ref) => {
  const str = useGeckoMeasurementLabelText(measurement, variant, showLoading)

  const AsType = div ? 'div' : 'span'
  return !str ? null : (
    <AsType ref={ref} {...rest}>
      {str}
    </AsType>
  )
})

GeckoMeasurementLabel.displayName = 'GeckoMeasurementLabel'
